import "moment/locale/pt-br";
import "react-datetime/css/react-datetime.css";
import React, { useEffect, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Flex,
  Input,
  FormControl,
  FormErrorMessage,
  FormLabel,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import DateTime from "react-datetime";
import { FilterContainer } from "components/Tables/FilterContainer";
import { LOCALE_PT_BR } from "constants/date-time";
import SearchSelect from "components/SearchSelect/SearchSelect";
import { processesService , PROCESS_TRIAL_STATUS} from "services/processesService";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { aitFilterMask, plateMask, validateVehiclePlate, processNumberFilterMask } from "utils/forms-utils";
import { parseDateBrToISO } from "utils/utils";

export const ProcessFilter = (props) => {
  const { t } = useTranslation('common');
  const { onFilter, filterSnapshot } = props;
  const { isOpen, onToggle } = useDisclosure();
  const showToast = useCustomToast();
  const [plate, setPlate] = useState("");
  const [aitNumber, setAitNumber] = useState("");
  const [processNumber, setProcessNumber] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [adminUnit, setAdminUnit] = useState("");
  const [responsiblePerson, setResponsiblePerson] = useState("");
  const [trial, setTrial] = useState("");
  const placeholderColor = useColorModeValue("gray.300", "gray.400");

  const [users, setUsers] = useState([]);
  const [adminUnits, setAdminUnits] = useState([]);

  const isStartDateInvalid = startDate ? startDate > new Date() : false;
  const isEndDateInvalid = startDate && endDate ? endDate <= startDate : false;
  const [plateError, setPlateError] = useState(null);

  const handleAitNumberChange = (e) => {
    setAitNumber(aitFilterMask(e.target.value));
  };

  const handlePlateChange = (e) => {
    const maskedPlate = plateMask(e.target.value);
    setPlate(maskedPlate);
    if (plateError) setPlateError(null);
  };

  const handleProcessNumberChange = (e) => {
    setProcessNumber(processNumberFilterMask(e.target.value));
  };

  const fetchUsers = async () => {
    try {
      const response = await processesService.lookupUsers();
      const userList = response || [];

      const users = userList.map(user => ({
        ...user,
        username: user.pessoa?.nome || user.login,
      }));

      setUsers(users);
    } catch (err) {
      showToast({ title: t(err), status: "error" });
    }
  };

  const fetchAdminUnits = async () => {
    try {
      const response = await processesService.listDepartments();
      const departmentList = response || [];
      setAdminUnits(departmentList);
    } catch (err) {
      showToast({ title: t(err), status: "error" });
    }
  };

  const onFilterHandler = () => {
    onFilter({
      placa: plate,
      numero_ait: aitNumber,
      numero: processNumber,
      data_inicial: startDate ? new Date(startDate).toLocaleDateString(LOCALE_PT_BR) : "",
      data_final: endDate ? new Date(endDate).toLocaleDateString(LOCALE_PT_BR) : "",
      id_unidade_administrativa_responsavel: adminUnit,
      id_usuario_responsavel_atual: responsiblePerson,
      julgamento: trial,
    });
  }

  const onClearHandler = () => {
    setPlate("");
    setAitNumber("");
    setStartDate("");
    setProcessNumber("")
    setEndDate("");
    setAdminUnit("");
    setResponsiblePerson("");
    setTrial("");
    setPlateError(null);

    onFilter({
      placa: null,
      numero_ait: null,
      numero: null,
      data_inicial: null,
      data_final: null,
      id_unidade_administrativa_responsavel: null,
      id_usuario_responsavel_atual: null,
      julgamento: null,
    });
  };

  const handleToggle = () => {
    if (isOpen) setPlateError(null);
    onToggle();
  };

  useEffect(() => {
    fetchUsers();
    fetchAdminUnits();
  }, []);

  useLayoutEffect(() => {
    if (filterSnapshot == null) {
      setPlate("");
      setAitNumber("");
      setProcessNumber("");
      setStartDate("");
      setEndDate("");
      setAdminUnit("");
      setResponsiblePerson("");
      setTrial("");
      return;
    }
    setPlate(filterSnapshot.placa ? String(filterSnapshot.placa) : "");
    setAitNumber(filterSnapshot.numero_ait ? String(filterSnapshot.numero_ait) : "");
    setProcessNumber(filterSnapshot.numero ? String(filterSnapshot.numero) : "");
    if (filterSnapshot.data_inicial) {
      const d = parseDateBrToISO(filterSnapshot.data_inicial);
      setStartDate(d && !isNaN(d.getTime()) ? d : "");
    } else {
      setStartDate("");
    }
    if (filterSnapshot.data_final) {
      const d = parseDateBrToISO(filterSnapshot.data_final);
      setEndDate(d && !isNaN(d.getTime()) ? d : "");
    } else {
      setEndDate("");
    }
    setAdminUnit(
      filterSnapshot.id_unidade_administrativa_responsavel != null && filterSnapshot.id_unidade_administrativa_responsavel !== ""
        ? String(filterSnapshot.id_unidade_administrativa_responsavel)
        : ""
    );
    setResponsiblePerson(
      filterSnapshot.id_usuario_responsavel_atual != null && filterSnapshot.id_usuario_responsavel_atual !== ""
        ? String(filterSnapshot.id_usuario_responsavel_atual)
        : ""
    );
    setTrial(filterSnapshot.julgamento != null && filterSnapshot.julgamento !== "" ? String(filterSnapshot.julgamento) : "");
  }, [filterSnapshot]);

  return (
    <FilterContainer
      w='125px'
      fontSize='sm'
      fontWeight='bold'
      variant="light"
      isOpen={isOpen}
      onToggle={handleToggle}
      onFilter={onFilterHandler}
      onClear={onClearHandler}
    >
      <FormControl isInvalid={!!plateError}>
        <FormLabel>
          {t("process.filters.fields.plate")}
        </FormLabel>
        <Input
          fontSize="sm"
          placeholder={t("process.filters.placeholders.plate")}
          _placeholder={{ color: placeholderColor, fontSize: "sm" }}
          value={plate}
          onBlur={() => {
            const err = validateVehiclePlate(plate, t("process.filters.fields.plate"));
            setPlateError(err);
          }}
          onChange={handlePlateChange}
        />
        <FormErrorMessage>
          {plateError && t(plateError)}
        </FormErrorMessage>
      </FormControl>

      <Flex w="100%" gap="4" justify="space-between">
        <FormControl>
          <FormLabel>
            {t("process.filters.fields.ait-number")}
          </FormLabel>
          <Input
            fontSize="sm"
            placeholder={t("process.filters.placeholders.ait-number")}
            _placeholder={{ color: placeholderColor, fontSize: "sm" }}
            value={aitNumber}
            onChange={handleAitNumberChange}
            maxLength={10}
          />
        </FormControl>

        <FormControl>
          <FormLabel>
            {t("process.filters.fields.process-number")}
          </FormLabel>
          <Input
            fontSize="sm"
            placeholder={t("process.filters.placeholders.process-number")}
            _placeholder={{ color: placeholderColor, fontSize: "sm" }}
            value={processNumber}
            onChange={handleProcessNumberChange}
            maxLength={13}
          />
        </FormControl>
      </Flex>

      <Flex w="100%" gap="4" justify="space-between">
        <FormControl w="230px" isInvalid={isStartDateInvalid}>
          <FormLabel>
            {t("system.labels.start-date")}
          </FormLabel>
          <DateTime
            locale={LOCALE_PT_BR}
            closeOnSelect
            timeFormat={false}
            inputProps={startDate ? undefined : { value: "", placeholder: t("system.placeholders.date") }}
            renderInput={(props) => (
              <Input
                {...props}
                fontSize="sm"
                _placeholder={{ color: placeholderColor, fontSize: "sm" }}
              />
            )}
            onChange={date => setStartDate(date)} />
          <FormErrorMessage>
            {t("system.messages.errors.invalid-start-date")}
          </FormErrorMessage>
        </FormControl>

        <FormControl w="230px" isInvalid={isEndDateInvalid}>
          <FormLabel>
            {t("system.labels.end-date")}
          </FormLabel>
          <DateTime
            locale={LOCALE_PT_BR}
            closeOnSelect
            timeFormat={false}
            inputProps={endDate ? undefined : { value: "", placeholder: t("system.placeholders.date") }}
            renderInput={(props) => (
              <Input
                {...props}
                fontSize="sm"
                _placeholder={{ color: placeholderColor, fontSize: "sm" }}
              />
            )}
            onChange={date => setEndDate(date)} />
          <FormErrorMessage>
            {t("system.messages.errors.invalid-end-date")}
          </FormErrorMessage>
        </FormControl>
      </Flex>

      <FormControl>
        <FormLabel>
          {t("process.filters.fields.responsible")}
        </FormLabel>
        <SearchSelect
          items={users}
          name="users"
          valueField="id"
          displayField="nome_pessoa"
          value={responsiblePerson}
          onChange={e => setResponsiblePerson(e.currentTarget.value)}
          placeholder={t("process.filters.placeholders.responsible")}
        />
      </FormControl>

      <FormControl>
        <FormLabel>
          {t("process.filters.fields.admin-unit")}
        </FormLabel>
        <SearchSelect
          items={adminUnits}
          name="adminUnits"
          valueField="id"
          displayField="descricao"
          value={adminUnit}
          onChange={e => setAdminUnit(e.currentTarget.value)}
          placeholder={t("process.filters.placeholders.admin-unit")}
        />
      </FormControl>

      <FormControl>
        <FormLabel>
          {t("process.filters.fields.trial")}
        </FormLabel>
        <SearchSelect
          items={Object.entries(PROCESS_TRIAL_STATUS).map(([id, nome]) => ({
            id,
            nome,
          }))}
          name="trials"
          valueField="id"
          displayField="nome"
          value={trial}
          onChange={e => setTrial(e.currentTarget.value)}
          placeholder={t("process.filters.placeholders.trial")}
        />
      </FormControl>
    </FilterContainer>
  );
}
